import { createContext, useCallback, useContext, useEffect, useMemo, useReducer, type FunctionComponent, type ReactNode } from 'react';
import moment from 'moment';
import { useGetIncidentReports } from 'api/queries/incidentReportQuery';
import { IncidentReportStatus, type CICaseDetail, type IncidentReport, type IncidentReportRiskAssessments, type IncidentReportSectionType } from 'components/CriticalIncident/interfaces';
import type { IncidentReportUpdatePayload } from 'api/types';
import { useFeatureManager } from 'hooks/useFeatureManager';
import { initialValues } from './schema';
import { ALL_SECTION_KEYS, useReportSectionsConfig, type ReportNavigationKey, type ReportMenuItem } from './reportSections';

export type { ReportNavigationKey } from './reportSections';

// older reports persisted these risk-factor-level fields as a single string; normalize to the array shape the multi-select UI now expects
function toRiskFactorLevelArray(value: unknown): string[] {
  if (Array.isArray(value)) return value as string[];
  return typeof value === 'string' && value ? [value] : [];
}

const SHORTENED_DATE_LENGTH = 10;

// a server-side bug returns some date fields as a date-only string (e.g. "2024-01-15") instead of a full
// ISO timestamp; consumers then parse that as UTC midnight, which shifts the date back a day in timezones
// ahead of UTC. Re-anchor these to the start of the day in local time before they reach the rest of the app.
function isShortenedDateString(value: unknown): value is string {
  return typeof value === 'string' && value.length === SHORTENED_DATE_LENGTH && moment(value, 'YYYY-MM-DD', true).isValid();
}

function convertShortenedDateFields<T>(value: T): T {
  if (isShortenedDateString(value)) {
    return moment(value).startOf('day').toDate().toISOString() as unknown as T;
  }
  if (Array.isArray(value)) {
    return value.map(convertShortenedDateFields) as unknown as T;
  }
  if (value && typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value).map(([key, val]) => [key, convertShortenedDateFields(val)])
    ) as T;
  }
  return value;
}

function normalizeRiskAssessment(riskAssessment: IncidentReportRiskAssessments): IncidentReportRiskAssessments {
  return {
    ...riskAssessment,
    participants: (riskAssessment.participants ?? []).map(participant => ({
      ...participant,
      suicideRiskFactors: toRiskFactorLevelArray(participant.suicideRiskFactors),
      selfHarmRiskFactors: toRiskFactorLevelArray(participant.selfHarmRiskFactors),
      harmToFromOthersRiskFactors: toRiskFactorLevelArray(participant.harmToFromOthersRiskFactors),
    })),
  };
}

interface IncidentReportState {
  errorSections: ReportNavigationKey[];
  hiddenMenuItems: ReportNavigationKey[];
  isLoading: boolean;
  report: IncidentReport | null;
  remoteReport: IncidentReport | null;
  hasAttemptedSubmit: boolean;
  affirmations: {
    submissionIsCorrect: boolean;
    timesheetSent: boolean;
    followUpsFinalised: boolean;
  }
}

type IncidentReportAction =
  | { type: 'SET_REPORT'; payload: IncidentReport | null }
  | { type: 'SET_REMOTE_REPORT'; payload: IncidentReport | null }
  | { type: 'UPDATE_SECTION'; payload: IncidentReportUpdatePayload }
  | { type: 'SET_ERRORS'; payload: ReportNavigationKey[] }
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'TOGGLE_MENU_VISIBILITY'; payload: { key: ReportNavigationKey; hidden: boolean } }
  | { type: 'UPDATE_AFFIRMATIONS', payload: Partial<IncidentReportState['affirmations']> }
  | { type: 'SET_SUBMIT_ATTEMPTED'; payload: boolean };

const initialState: IncidentReportState = {
  errorSections: [],
  hiddenMenuItems: ['review'],
  isLoading: true,
  report: null,
  remoteReport: null,
  hasAttemptedSubmit: false,
  affirmations: {
    submissionIsCorrect: false,
    timesheetSent: false,
    followUpsFinalised: false,
  },
};

function incidentReportReducer(state: IncidentReportState, action: IncidentReportAction): IncidentReportState {
  switch (action.type) {
    case 'SET_REPORT':
      return { ...state, report: action.payload };
    case 'SET_REMOTE_REPORT':
      return { ...state, remoteReport: action.payload };
    case 'UPDATE_SECTION': {
      if (!state.report) return state;
      const { section, ...sectionData } = action.payload;
      return { ...state, report: { ...state.report, [section]: sectionData } as IncidentReport };
    }
    case 'SET_ERRORS':
      return { ...state, errorSections: action.payload };
    case 'SET_LOADING':
      return { ...state, isLoading: action.payload };
    case 'TOGGLE_MENU_VISIBILITY': {
      const { key, hidden } = action.payload;
      return {
        ...state,
        hiddenMenuItems: hidden
          ? [...state.hiddenMenuItems, key]
          : state.hiddenMenuItems.filter(k => k !== key),
      };
    }
    case 'UPDATE_AFFIRMATIONS':
      return { ...state, affirmations: { ...state.affirmations, ...action.payload } };
    case 'SET_SUBMIT_ATTEMPTED':
      return { ...state, hasAttemptedSubmit: action.payload };
    default:
      return state;
  }
}

interface IncidentReportContextValue extends IncidentReportState {
  reportSections: ReportMenuItem[];
  isAU: boolean;
  setReport: (payload: IncidentReport) => void;
  updateReport: (payload: IncidentReportUpdatePayload) => void;
  updateErrors: (payload: ReportNavigationKey[]) => void;
  setIsLoading: (payload: boolean) => void;
  toggleMenuVisibility: (key: ReportNavigationKey, hidden: boolean) => void;
  updateAffirmation: (affirmation: Partial<IncidentReportState['affirmations']>) => void;
  setSubmitAttempted: (payload: boolean) => void;
}

const IncidentReportContext = createContext<IncidentReportContextValue | undefined>(undefined);

export const useIncidentReportContext = () => {
  const context = useContext(IncidentReportContext);
  if (context === undefined) {
    throw new Error('useIncidentReport must be used within an IncidentReportContextProvider');
  }
  return context;
};

export const IncidentReportContextProvider: FunctionComponent<{ ciCase: CICaseDetail; children: ReactNode }> = ({ ciCase, children }) => {
  const [state, dispatch] = useReducer(incidentReportReducer, initialState);
  const { isIncidentReportFeatureEnabled } = useFeatureManager();
  const { isAU, reportSections, allSections } = useReportSectionsConfig(ciCase.providerId);
  const { data: incidentReports, error } = useGetIncidentReports(ciCase.caseId, ciCase.activityId, undefined, isIncidentReportFeatureEnabled());
  const mergeReports = (report?: IncidentReport) => {
    // the API can return sections as `{}` instead of omitting/nulling them; a flat spread would let
    // an empty section object wipe out that section's default field shape, so merge each section individually
    const mergedSections = Object.fromEntries(
      ALL_SECTION_KEYS.map(section => {
        const nonNullFields = Object.fromEntries(
          Object.entries(report?.[section] ?? {}).filter(([, value]) => value !== null)
        );
        const merged = { ...initialValues[section], ...nonNullFields };
        return [section, section === 'riskAssessment' ? normalizeRiskAssessment(merged as unknown as IncidentReportRiskAssessments) : merged];
      })
    );

    return {
      ...initialValues,
      ...report,
      ...mergedSections,
      activityId: ciCase.activityId,
      caseId: ciCase.caseId,
    } as IncidentReport;
  };

  useEffect(() => {
    if (incidentReports === undefined) {
      const emptyReport = mergeReports();
      dispatch({ type: 'SET_REPORT', payload: emptyReport });
      dispatch({ type: 'SET_REMOTE_REPORT', payload: emptyReport });
      if (error) {
        dispatch({ type: 'SET_LOADING', payload: false });
      }
      return;
    }
    const [rawIncidentReport] = incidentReports;
    const incidentReport = rawIncidentReport ? convertShortenedDateFields(rawIncidentReport) : mergeReports();
    const fullReport = mergeReports(incidentReport);
    dispatch({ type: 'SET_REMOTE_REPORT', payload: fullReport }); // this is only called after the API updates the report. Never exported as a context mutation function
    dispatch({ type: 'SET_REPORT', payload: fullReport });
    dispatch({ type: 'SET_LOADING', payload: false });
    // we only want to show the review section when all other sections have been filled
    const filledOutSections = Object.keys(incidentReport).filter(sect => !!incidentReport[sect] && allSections.includes(sect as IncidentReportSectionType));
    if (fullReport?.status === IncidentReportStatus.InProgress && allSections.every(section => filledOutSections.includes(section))) {
      dispatch({ type: 'TOGGLE_MENU_VISIBILITY', payload: { key: 'review', hidden: false } });
    }
  }, [incidentReports, error]);

  useEffect(() => {
    dispatch({ type: 'SET_SUBMIT_ATTEMPTED', payload: false });
  }, [ciCase.caseId, ciCase.activityId]);

  const setReport = useCallback((payload: IncidentReport) => {
    dispatch({ type: 'SET_REPORT', payload });
  }, []);

  const updateReport = useCallback((payload: IncidentReportUpdatePayload) => {
    dispatch({ type: 'UPDATE_SECTION', payload });
  }, []);

  const updateErrors = useCallback((payload: ReportNavigationKey[]) => {
    dispatch({ type: 'SET_ERRORS', payload });
  }, []);

  const setIsLoading = useCallback((payload: boolean) => {
    dispatch({ type: 'SET_LOADING', payload });
  }, []);

  const toggleMenuVisibility = useCallback((key: ReportNavigationKey, hidden: boolean) => {
    dispatch({ type: 'TOGGLE_MENU_VISIBILITY', payload: { key, hidden } });
  }, []);

  const updateAffirmation = useCallback((affirmation: Partial<IncidentReportState['affirmations']>) => {
    dispatch({ type: 'UPDATE_AFFIRMATIONS', payload: affirmation });
  }, []);

  const setSubmitAttempted = useCallback((payload: boolean) => {
    dispatch({ type: 'SET_SUBMIT_ATTEMPTED', payload });
  }, []);

  const value = useMemo(() => ({
    ...state,
    reportSections,
    isAU,
    setReport,
    updateReport,
    updateErrors,
    setIsLoading,
    toggleMenuVisibility,
    updateAffirmation,
    setSubmitAttempted
  }), [state, reportSections, isAU, setReport, updateReport, updateErrors, setIsLoading, toggleMenuVisibility, updateAffirmation, setSubmitAttempted]);

  return (
    <IncidentReportContext.Provider value={value}>
      {children}
    </IncidentReportContext.Provider>
  );
};
